import React, { useMemo } from "react";
import { valueToString } from "helpers/Helpers";
import classNames from "classnames";

import BaseReportTable from "./BaseReportTable";

import "components/reportTable/reportTable.scss";
import { formatReportCurrency } from "helpers/reportFormat";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface IProps {
    data: Array<Record<string, any>>;
    columns: Array<Record<string, any>>;
    description: string;
    compileDate: string;
}

export default function LoyaltyProgramRedemptionsTable(props: IProps) {

    const tableRows = useMemo(() => props.data.filter(row => !row.is_total_row), [props.data]);
    const {t} = useTranslation();

    function formatDisplay(type: string, value: string) {
        let newValue = value !== null && value !== undefined ? value : "";

        if (newValue !== "") {
            switch (type) {
                case "currency":
                    newValue = formatReportCurrency(value);
                    break;
                case "decimal":
                    newValue = Number(value).toFixed(2);
                    break;
                case "percentage":
                    newValue = `${Number(value) * 100}%`;
                    break;
                case "datetime":
                    newValue = moment.utc(value, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LLL");
                    break;
                case "loyalty_points":
                    newValue = `${newValue} ${t("pts")}`;
                    break;
                default:
                    break;
            }
        }

        return newValue;
    }

    // Format cell data based on column
    function cellData(columnId: string, cellValue: string, displayType: string) {
        switch (columnId) {
            case "order_name":
                return formatDisplay(displayType, String(cellValue));
            case "payment_type":
                return formatDisplay(displayType, valueToString(cellValue));
            default:
                return formatDisplay(displayType, cellValue);
        }
    }

    function cellClassNames(cell: any, isRowLocked: boolean) {
        return classNames({
            "report-table-row-locked": isRowLocked,
            "report-table-cell-clickable": cell.column.id === "order_name" || cell.column.id === "customer_name",
        });
    }

    // Add on click events based on column
    function cellOnClick(cell: any) {
        if (!cell) {
            return null;
        }
        switch (cell.column.id) {
            case "order_name":
                return cell.row.original.order_id
                    ? window.open(`/admin/order/${String(cell.row.original.order_id)}`)
                    : null;
            case "customer_name":
                return cell.row.original.customer_id
                    ? window.open(`/admin/customers/${String(cell.row.original.customer_id)}/profile`)
                    : null;
            default:
                return null;
        }
    }

    // Add footer data based on column
    function footerData(columnId: string, footerRow?: Record<string, any>) {
        if (!footerRow) {
            return null;
        }

        switch (columnId) {
            case "order_name":
                return footerRow?.order_name as string;

            case "application_amount":
            case "amount":
                return formatDisplay(
                    "currency",
                    String(footerRow[columnId] ?? 0),
                );

            default:
                return footerRow[columnId] as string ?? null;
        }
    }

    return (
        <BaseReportTable
            data={tableRows}
            columns={props.columns}
            description={props.description}
            compileDate={props.compileDate}
            cellOnClick={cellOnClick}
            cellData={cellData}
            cellClassNames={cellClassNames}
            footerData={footerData}
            expanding={false}
        />
    );
}
